// reciboCarrito.js
// Uso: import { generarReciboCarrito } from './reciboCarrito';
// Requiere: npm install jspdf

import { jsPDF } from 'jspdf';

const VERDE      = [130, 216, 179];   // #82D8B3
const NEGRO      = [17,  22,  20];   // #111614
const VERDE_DK   = [77,  184, 138];  // #4db88a
const GRIS_TXT   = [90,  106,  99];  // #5a6a63
const GRIS_LT    = [243, 248, 245];  // #f3f8f5
const BLANCO     = [255, 255, 255];

// Carga el logo una sola vez y lo cachea como dataURL (para jsPDF.addImage)
let logoPromiseCache = null;
function getLogo() {
  if (!logoPromiseCache) {
    logoPromiseCache = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataURL: canvas.toDataURL('image/png'), ratio: img.naturalWidth / img.naturalHeight });
      };
      img.onerror = () => resolve(null);
      img.src = '/images/logo_centro.png';
    });
  }
  return logoPromiseCache;
}

/**
 * generarReciboCarrito({ pedido, items, form, metodo, descuento, cuponAplicado, totalFinal })
 * Genera y descarga automáticamente el recibo en PDF.
 */
export async function generarReciboCarrito({
  pedido,
  items,
  form,
  metodo,
  descuento = 0,
  cuponAplicado = null,
  totalFinal,
}) {
  const logo = await getLogo();
  const doc  = new jsPDF({ unit: 'mm', format: 'a4' });


  const W    = 210;
  const PAD  = 18;
  const ANCHO = W - PAD * 2;
  let y      = 0;

  // ── utilidades ──────────────────────────────────────────────
  const rect  = (x, yy, w, h, r = []) => doc.roundedRect(x, yy, w, h, r[0]||0, r[1]||0, 'F');
  const line  = (x1, yy, x2) => { doc.setDrawColor(...GRIS_LT); doc.setLineWidth(0.2); doc.line(x1, yy, x2, yy); };
  const text  = (str, x, yy, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || NEGRO));
    if (opts.align === 'right') doc.text(str, x, yy, { align: 'right' });
    else if (opts.align === 'center') doc.text(str, x, yy, { align: 'center' });
    else doc.text(str, x, yy);
  };
  const hoy   = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  };
  const metodoPago = metodo === 'yape'    ? 'Yape / Transferencia'
                   : metodo === 'tarjeta' ? 'Tarjeta (Izipay)'
                   : metodo === 'culqi'   ? 'Tarjeta (Culqi)'
                   : metodo;

   // ── HEADER negro ────────────────────────────────────────────
   doc.setFillColor(...NEGRO);
   doc.rect(0, 0, W, 38, 'F');
 
   let textStartX;
   if (logo) {
     const logoH = 11;
     const logoW = logoH * logo.ratio;
     doc.addImage(logo.dataURL, 'PNG', PAD, 13.5, logoW, logoH);
     textStartX = PAD + logoW + 6;
   } else {
     doc.setFillColor(...VERDE);
     doc.circle(PAD + 8, 19, 8, 'F');
     doc.setFontSize(14);
     doc.setTextColor(...NEGRO);
     doc.setFont('helvetica', 'bold');
     doc.text('C', PAD + 5.2, 22.5);
     textStartX = PAD + 20;
   }

  // razón social
  /*text('COMUNICAR DESARROLLO Y LENGUAJE E.I.R.L.', PAD + 20, 15, { bold: true, size: 9, color: BLANCO });
  text('RUC: 20615646939', PAD + 20, 21, { size: 8, color: VERDE });
  text('Centro Comunicar  ·  centrocomunicar.com', PAD + 20, 27, { size: 7.5, color: [180, 200, 190] }); */

  // número recibo
  const nroRecibo = pedido?.codigo ? `R-${pedido.codigo}` : 'R-PENDIENTE';
  text('Recibo de pago', W - PAD, 15, { align: 'right', size: 8, color: [180, 200, 190] });
  text(nroRecibo, W - PAD, 22, { align: 'right', bold: true, size: 10, color: BLANCO });

  y = 38;

  // ── BANDA VERDE ─────────────────────────────────────────────
  doc.setFillColor(...VERDE);
  doc.rect(0, y, W, 9, 'F');
  text('RECIBO DE PAGO', PAD, y + 6, { bold: true, size: 8, color: [15, 53, 37] });
  text(hoy(), W - PAD, y + 6, { align: 'right', size: 8, color: [15, 53, 37] });
  y += 9;

  // ── DATOS PEDIDO / CLIENTE ───────────────────────────────────
  y += 8;
  const COL2 = PAD + ANCHO / 2;

  const campo = (label, valor, x, yy) => {
    text(label, x, yy, { size: 7.5, color: GRIS_TXT });
    text(valor || '—', x, yy + 5, { bold: true, size: 9, color: NEGRO });
  };

  campo('Fecha de emisión', hoy(),                         PAD,  y);
  campo('Código de pedido', pedido?.codigo || '—',         COL2, y);
  y += 12;
  campo('Cliente',          form?.nombre   || '—',         PAD,  y);
  campo('Correo',           form?.email    || '—',         COL2, y);
  y += 12;
  campo('Método de pago',   metodoPago,                    PAD,  y);

  // badge estado
  doc.setFillColor(...[212, 244, 232]);
  doc.roundedRect(COL2, y - 1, 38, 7, 3, 3, 'F');
  text('✓  Pagado', COL2 + 4, y + 4.5, { size: 7.5, color: [15, 53, 37], bold: true });

  y += 14;
  line(PAD, y, W - PAD);
  y += 6;

  // ── TABLA ITEMS ─────────────────────────────────────────────
  // cabecera tabla
  doc.setFillColor(...GRIS_LT);
  doc.rect(PAD, y, ANCHO, 7, 'F');
  text('Descripción',  PAD + 2,          y + 5, { size: 7.5, color: GRIS_TXT, bold: true });
  text('Cant.',        PAD + ANCHO * .68, y + 5, { size: 7.5, color: GRIS_TXT, bold: true, align: 'center' });
  text('P. Unit.',     PAD + ANCHO * .82, y + 5, { size: 7.5, color: GRIS_TXT, bold: true, align: 'right' });
  text('Total',        W - PAD,           y + 5, { size: 7.5, color: GRIS_TXT, bold: true, align: 'right' });
  y += 7;

  // filas
  items.forEach((item, idx) => {
    if (idx % 2 === 0) {
      doc.setFillColor(250, 252, 251);
      doc.rect(PAD, y, ANCHO, 9, 'F');
    }
    // título con wrap
    const titulo = doc.splitTextToSize(item.titulo || '—', ANCHO * 0.60);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...NEGRO);
    doc.text(titulo, PAD + 2, y + 6);

    text('1',                              PAD + ANCHO * .68, y + 6, { size: 8.5, color: GRIS_TXT, align: 'center' });
    text(`S/ ${item.precio.toFixed(2)}`,   PAD + ANCHO * .82, y + 6, { size: 8.5, align: 'right' });
    text(`S/ ${item.precio.toFixed(2)}`,   W - PAD,           y + 6, { size: 8.5, bold: true, align: 'right' });

    y += titulo.length > 1 ? titulo.length * 5 + 4 : 10;
  });

  y += 4;
  line(PAD, y, W - PAD);
  y += 6;

  // ── TOTALES ─────────────────────────────────────────────────
  const subtotal = items.reduce((s, i) => s + i.precio, 0);
  const TW = 80;
  const TX = W - PAD - TW;

  const fila = (label, valor, bold = false, color = NEGRO) => {
    text(label, TX, y, { size: 8.5, color: GRIS_TXT });
    text(valor,  W - PAD, y, { size: 8.5, align: 'right', bold, color });
    y += 6;
  };

  fila('Subtotal', `S/ ${subtotal.toFixed(2)}`);

  if (descuento > 0 && cuponAplicado) {
    fila(`Descuento (${cuponAplicado.codigo})`, `- S/ ${descuento.toFixed(2)}`, false, VERDE_DK);
  }

  line(TX, y, W - PAD);
  y += 5;
  text('Total pagado', TX, y, { size: 10, bold: true });
  text(`S/ ${totalFinal.toFixed(2)}`, W - PAD, y, { size: 11, bold: true, align: 'right', color: VERDE_DK });
  y += 10;

  // ── NOTA LEGAL ──────────────────────────────────────────────
  line(PAD, y, W - PAD);
  y += 6;
  doc.setFillColor(...GRIS_LT);
  doc.roundedRect(PAD, y, ANCHO, 14, 3, 3, 'F');
  text('Este recibo acredita el pago de productos digitales de Centro Comunicar.', PAD + 4, y + 5.5, { size: 7.5, color: GRIS_TXT });
  text('Los productos digitales no requieren envío físico. Acceso disponible tras validar el pago.',  PAD + 4, y + 10.5, { size: 7.5, color: GRIS_TXT });
  y += 20;

  // ── FOOTER negro ────────────────────────────────────────────
  const pageH = 297;
  doc.setFillColor(...NEGRO);
  doc.rect(0, pageH - 14, W, 14, 'F');
  text(
    'Gracias por tu compra  ·  COMUNICAR DESARROLLO Y LENGUAJE E.I.R.L.  ·  RUC 20615646939',
    W / 2, pageH - 5.5,
    { align: 'center', size: 7.5, color: [150, 170, 160] }
  );

  // ── DESCARGAR ───────────────────────────────────────────────
  const filename = `Recibo-${pedido?.codigo || 'ComunicarCC'}.pdf`;
  doc.save(filename);
}